const sanitizePdfFilename = (title) => {
  const base = (title || 'documentacion-teccc')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_\s]/g, ' ')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase();

  return base || 'documentacion-teccc';
};

const getExportContentWidth = (rootElement) => {
  const articleWidth = rootElement?.clientWidth || 0;
  return Math.max(articleWidth - 32, 480);
};

const computeExportSize = (naturalWidth, naturalHeight, maxWidth) => {
  const scale = naturalWidth > maxWidth ? maxWidth / naturalWidth : 1;
  return {
    exportWidth: Math.ceil(naturalWidth * scale),
    exportHeight: Math.ceil(naturalHeight * scale),
  };
};

const getMermaidCaptureSize = (viewport, host, svg, maxWidth) => {
  const svgRect = svg.getBoundingClientRect();
  const contentWidth = Math.ceil(Math.max(svgRect.width, host.getBoundingClientRect().width, 1));
  const contentHeight = Math.ceil(Math.max(svgRect.height, host.getBoundingClientRect().height, 1));
  return computeExportSize(contentWidth, contentHeight, maxWidth);
};

const MAX_RASTER_DIMENSION = 4096;

const computeRasterScale = (exportWidth, exportHeight, preferredScale = 2) => {
  const scaleByWidth = MAX_RASTER_DIMENSION / exportWidth;
  const scaleByHeight = MAX_RASTER_DIMENSION / exportHeight;
  const cappedScale = Math.min(preferredScale, scaleByWidth, scaleByHeight);
  return Math.max(cappedScale, 0.75);
};

const rasterizeMermaidBlock = async (viewport, host, svg, maxWidth, html2canvas) => {
  const { exportWidth, exportHeight } = getMermaidCaptureSize(viewport, host, svg, maxWidth);
  const scale = computeRasterScale(exportWidth, exportHeight);
  const captureHeight = exportHeight + 16;

  const prev = {
    viewportOverflow: viewport.style.overflow,
    viewportWidth: viewport.style.width,
    viewportMinHeight: viewport.style.minHeight,
    hostWidth: host.style.width,
    hostMinHeight: host.style.minHeight,
  };

  viewport.style.overflow = 'visible';
  viewport.style.width = `${exportWidth}px`;
  viewport.style.minHeight = '';
  host.style.width = `${exportWidth}px`;
  host.style.minHeight = '';

  try {
    const canvas = await html2canvas(viewport, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      scrollX: 0,
      scrollY: -window.scrollY,
      width: exportWidth,
      height: captureHeight,
      windowWidth: exportWidth,
    });

    return {
      dataUrl: canvas.toDataURL('image/jpeg', 0.92),
      width: exportWidth,
      height: exportHeight,
    };
  } finally {
    viewport.style.overflow = prev.viewportOverflow;
    viewport.style.width = prev.viewportWidth;
    viewport.style.minHeight = prev.viewportMinHeight;
    host.style.width = prev.hostWidth;
    host.style.minHeight = prev.hostMinHeight;
  }
};

const applyMermaidRaster = ({ host, svg, viewport, wrapper, dataUrl, width, height }) => {
  const img = document.createElement('img');
  img.className = 'docs-mermaid-raster';
  img.alt = 'Diagrama';
  img.style.display = 'block';
  img.style.width = '100%';
  img.style.maxWidth = `${width}px`;
  img.style.height = 'auto';
  img.src = dataUrl;

  host.innerHTML = '';
  host.appendChild(img);
  host.style.width = '100%';
  host.style.maxWidth = `${width}px`;
  host.style.minHeight = '';

  if (viewport) {
    viewport.style.width = '100%';
    viewport.style.maxWidth = `${width}px`;
    viewport.style.minHeight = '';
    viewport.style.overflow = 'visible';
  }

  if (wrapper) {
    wrapper.style.width = '100%';
    wrapper.style.maxWidth = `${width}px`;
    wrapper.style.minHeight = '';
    wrapper.style.pageBreakInside = 'auto';
    wrapper.style.breakInside = 'auto';
  }

  return { host, svg, viewport, wrapper };
};

const prepareMermaidForExport = async (rootElement) => {
  const restored = [];
  const wrappers = [...rootElement.querySelectorAll('.docs-mermaid-wrapper')];
  const maxWidth = getExportContentWidth(rootElement);

  if (wrappers.length === 0) {
    return () => {};
  }

  const html2canvasModule = await import('html2canvas');
  const html2canvas = html2canvasModule.default || html2canvasModule;

  for (const wrapper of wrappers) {
    const viewport = wrapper.querySelector('.docs-mermaid-viewport');
    const host = wrapper.querySelector('.docs-mermaid-svg-host');
    const svg = host?.querySelector('svg');

    if (!viewport || !host || !svg) continue;

    try {
      const raster = await rasterizeMermaidBlock(viewport, host, svg, maxWidth, html2canvas);
      restored.push(applyMermaidRaster({
        host,
        svg,
        viewport,
        wrapper,
        dataUrl: raster.dataUrl,
        width: raster.width,
        height: raster.height,
      }));
    } catch (rasterError) {
      console.warn('[documentation] Mermaid raster skipped (frontend PDF)', rasterError);
    }
  }

  return () => {
    restored.forEach(({ host, svg, viewport, wrapper }) => {
      host.innerHTML = '';
      host.appendChild(svg);
      host.removeAttribute('style');
      viewport?.removeAttribute('style');
      wrapper?.removeAttribute('style');
    });
  };
};

const prepareElementForPdf = (rootElement) => {
  if (!rootElement) return () => {};

  const restored = [];

  rootElement.querySelectorAll('.docs-no-print').forEach((node) => {
    restored.push([node, 'display', node.style.display]);
    node.style.display = 'none';
  });

  rootElement.querySelectorAll('.docs-section-break').forEach((node) => {
    restored.push([node, 'display', node.style.display]);
    node.style.display = 'none';
  });

  rootElement.querySelectorAll('.docs-mermaid-viewport').forEach((node) => {
    restored.push([node, 'maxHeight', node.style.maxHeight]);
    restored.push([node, 'overflow', node.style.overflow]);
    node.style.maxHeight = 'none';
    node.style.overflow = 'visible';
  });

  rootElement.querySelectorAll('.docs-mermaid-wrapper').forEach((node) => {
    restored.push([node, 'pageBreakInside', node.style.pageBreakInside]);
    restored.push([node, 'breakInside', node.style.breakInside]);
    node.style.pageBreakInside = 'auto';
    node.style.breakInside = 'auto';
  });

  rootElement.querySelectorAll('.docs-table-wrap').forEach((node) => {
    restored.push([node, 'overflow', node.style.overflow]);
    node.style.overflow = 'visible';
  });

  rootElement.querySelectorAll('.docs-keep-with-next').forEach((node) => {
    restored.push([node, 'pageBreakAfter', node.style.pageBreakAfter]);
    node.style.pageBreakAfter = 'auto';
    node.style.breakAfter = 'auto';
  });

  rootElement.querySelectorAll('.docs-table-page-break').forEach((node) => {
    restored.push([node, 'pageBreakBefore', node.style.pageBreakBefore]);
    node.style.pageBreakBefore = 'auto';
    node.style.breakBefore = 'auto';
  });

  rootElement.querySelectorAll('.docs-code-wrap pre').forEach((node) => {
    restored.push([node, 'paddingTop', node.style.paddingTop]);
    restored.push([node, 'paddingBottom', node.style.paddingBottom]);
    restored.push([node, 'marginTop', node.style.marginTop]);
    restored.push([node, 'marginBottom', node.style.marginBottom]);
    node.style.paddingTop = '14px';
    node.style.paddingBottom = '14px';
    node.style.marginTop = '8px';
    node.style.marginBottom = '8px';
  });

  rootElement.querySelectorAll('.docs-mermaid-wrapper, .docs-mermaid-viewport, .docs-mermaid-svg-host').forEach((node) => {
    restored.push([node, 'minHeight', node.style.minHeight]);
    node.style.minHeight = '';
  });

  rootElement.style.width = `${getExportContentWidth(rootElement)}px`;
  restored.push([rootElement, 'width', '']);

  return () => {
    const toKebab = (prop) => prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

    restored.forEach(([node, prop, value]) => {
      if (!value) {
        node.style.removeProperty(toKebab(prop));
      } else {
        node.style[prop] = value;
      }
    });
  };
};

const truncateFooterTitle = (title, maxLen = 55) => {
  const normalized = String(title || 'Documentación TECCC').trim();
  if (normalized.length <= maxLen) return normalized;
  return `${normalized.slice(0, maxLen - 1)}…`;
};

const buildPdfFooterText = (page, totalPages, title) => {
  const safeTitle = truncateFooterTitle(title);
  return `Página ${page} de ${totalPages} · TECCC · ${safeTitle}`;
};

const addPageFootersToPdf = (pdf, title) => {
  const totalPages = pdf.internal.getNumberOfPages();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    pdf.setFontSize(9);
    pdf.setTextColor(102, 102, 102);
    pdf.text(buildPdfFooterText(page, totalPages, title), pageWidth / 2, pageHeight - 7, {
      align: 'center',
      maxWidth: pageWidth - 20,
    });
  }
};

/** Exporta PDF en el navegador (html2pdf.js + html2canvas). */
export const exportDocumentationToPdfFrontend = async (sourceElement, { title } = {}) => {
  if (!sourceElement) {
    throw new Error('docs-pdf-missing-element');
  }

  document.body.classList.add('docs-pdf-export-mode');
  const restoreStyles = prepareElementForPdf(sourceElement);
  let restoreMermaid = () => {};

  try {
    restoreMermaid = await prepareMermaidForExport(sourceElement);

    const html2pdfModule = await import('html2pdf.js');
    const html2pdf = html2pdfModule.default || html2pdfModule;
    const contentWidth = getExportContentWidth(sourceElement);

    const options = {
      margin: [12, 10, 22, 10],
      filename: `${sanitizePdfFilename(title)}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        scrollX: 0,
        scrollY: -window.scrollY,
        backgroundColor: '#ffffff',
        windowWidth: contentWidth,
        width: contentWidth,
        onclone: (clonedDoc) => {
          const clonedRoot = clonedDoc.getElementById('doc-print-area');
          const maxWidth = clonedRoot ? getExportContentWidth(clonedRoot) : contentWidth;

          if (clonedRoot) {
            clonedRoot.style.width = `${maxWidth}px`;
            clonedRoot.style.maxWidth = `${maxWidth}px`;
          }

          clonedDoc.querySelectorAll('.docs-section-break').forEach((node) => {
            node.style.display = 'none';
          });

          clonedDoc.querySelectorAll('.docs-mermaid-raster').forEach((img) => {
            img.style.display = 'block';
            img.style.width = '100%';
            img.style.maxWidth = `${maxWidth}px`;
            img.style.height = 'auto';
          });

          clonedDoc.querySelectorAll('.docs-mermaid-wrapper, .docs-mermaid-viewport, .docs-mermaid-svg-host').forEach((node) => {
            node.style.minHeight = '';
          });

          clonedDoc.querySelectorAll('.docs-keep-with-next').forEach((node) => {
            node.style.pageBreakAfter = 'auto';
            node.style.breakAfter = 'auto';
          });

          clonedDoc.querySelectorAll('.docs-table-page-break').forEach((node) => {
            node.style.pageBreakBefore = 'auto';
            node.style.breakBefore = 'auto';
          });

          clonedDoc.querySelectorAll('.docs-code-wrap pre').forEach((node) => {
            node.style.paddingTop = '14px';
            node.style.paddingBottom = '14px';
            node.style.marginTop = '8px';
            node.style.marginBottom = '8px';
          });

          clonedDoc.querySelectorAll('.docs-mermaid-wrapper').forEach((node) => {
            node.style.width = '100%';
            node.style.maxWidth = `${maxWidth}px`;
            node.style.minHeight = '';
            node.style.pageBreakInside = 'auto';
            node.style.breakInside = 'auto';
          });
        },
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      },
      pagebreak: {
        mode: ['css', 'legacy'],
        before: [],
        after: [],
        avoid: [],
      },
    };

    await html2pdf()
      .set(options)
      .from(sourceElement)
      .toPdf()
      .get('pdf')
      .then((pdf) => {
        addPageFootersToPdf(pdf, title);
      })
      .save();
  } finally {
    restoreMermaid();
    restoreStyles();
    document.body.classList.remove('docs-pdf-export-mode');
  }
};
